import React, { useState, useEffect, useMemo } from "react";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridToolbarQuickFilter,
    GridToolbarContainer,
    GridToolbar,
    GridFeatureMode,
    GridSortModel,
} from "@mui/x-data-grid";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { useTranslate } from "src/locales";
import ByzMuiOpenProcessMenu from "../../ByzMuiOpenProcessMenu";

export interface ByzDataTreeListGridProps {
    initialData: any[];
    columns: GridColDef[];
    treeColumn?: string;
    parentIdField?: string;
    idField?: string;
    nameField?: string;
    defaultExpandAll?: boolean;
    autoHeight?: boolean;
    checkboxSelection?: boolean;
    whiteGrid?: boolean;
    sx?: any;
    onSelectionChange?: (ids: any[]) => void;
    selectedIds?: any[];
    isRowSelectable?: (params: any) => boolean;
    getRowClassName?: (params: any) => string;
    customToolbar?: any;
    showFooter?: boolean;
    hideColumns?: boolean;
    actionColumnPosition?: "start" | "end";
    isActionColumnShow?: boolean;
    actionMenuItems?: (row: any) => { label: string; onClick: (row: any) => void; disabled?: boolean }[];
    headerActionMenuItems?: { label: string; onClick: () => void; disabled?: boolean }[];
    onRowClick?: (row: any) => void;
    pagination?: boolean;
    loading?: boolean;
    showDefaultToolbarButtons?: boolean;
    onSearchChange?: (value: string) => void;
    searchTerm?: string;
    useLConnector?: boolean;
    useSubdirectoryArrow?: boolean;
    indentColumns?: string[];
    initialState?: any;
    sortingMode?: GridFeatureMode;
    onSortModelChange?: (model: GridSortModel) => void;
    /** Footer satır sayısı seçenekleri (varsayılan: 5, 10, 20, 50, 100) */
    pageSizeOptions?: number[];
    [key: string]: any;
}

function CustomToolbarDef({ searchInput, onSearchInputChange, showButtons }: {
    searchInput: string;
    onSearchInputChange: (value: string) => void;
    showButtons?: boolean;
}) {
    const { t } = useTranslate();
    const [localInput, setLocalInput] = useState(searchInput);

    useEffect(() => {
        setLocalInput(searchInput);
    }, [searchInput]);

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            onSearchInputChange(localInput);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalInput(event.target.value);
    };

    return (
        <GridToolbarContainer sx={{ color: "#00B8D9" }}>
            {showButtons && <GridToolbar />}
            <GridToolbarQuickFilter
                placeholder={t("search") + " (Enter)"}
                sx={{ ml: "auto" }}
                value={localInput}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
            />
        </GridToolbarContainer>
    );
}

const InternalToolbar = (props: any) => {
    const {
        customToolbar: CustomToolbar,
        searchTerm,
        onSearchChange,
        showDefaultToolbarButtons
    } = props;

    if (CustomToolbar) {
        return (
            <CustomToolbar
                {...props}
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
            />
        );
    }

    return (
        <CustomToolbarDef
            searchInput={searchTerm}
            onSearchInputChange={onSearchChange}
            showButtons={showDefaultToolbarButtons}
        />
    );
};

const ByzDataTreeListGrid: React.FC<ByzDataTreeListGridProps> = ({
    initialData,
    columns,
    treeColumn,
    parentIdField = "parentId",
    idField = "id",
    nameField = "name",
    defaultExpandAll = false,
    autoHeight = false,
    checkboxSelection = false,
    whiteGrid = false,
    sx,
    onSelectionChange,
    selectedIds: propSelectedIds,
    isRowSelectable,
    getRowClassName,
    customToolbar,
    showFooter = true,
    hideColumns = false,
    actionColumnPosition = "start",
    isActionColumnShow = false,
    actionMenuItems,
    headerActionMenuItems,
    onRowClick,
    pagination = true,
    loading = false,
    showDefaultToolbarButtons = true,
    onSearchChange: externalOnSearchChange,
    searchTerm: externalSearchTerm,
    useLConnector = true,
    useSubdirectoryArrow = true,
    indentColumns = [],
    initialState,
    sortingMode,
    onSortModelChange,
    pageSizeOptions = [5, 10, 20, 50, 100],
    ...rest
}) => {
    const { t } = useTranslate();
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const [expandedRows, setExpandedRows] = useState<Record<any, boolean>>({});
    const [selectedIds, setSelectedIds] = useState<any[]>([]);
    const [internalSearchTerm, setInternalSearchTerm] = useState<string>("");

    const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
    const setSearchTerm = externalOnSearchChange !== undefined ? externalOnSearchChange : setInternalSearchTerm;

    useEffect(() => {
        if (propSelectedIds !== undefined) {
            setSelectedIds(propSelectedIds);
        }
    }, [propSelectedIds]);

    // Handle tree expansion logic for search
    useEffect(() => {
        if (searchTerm && searchTerm.trim() !== '') {
            const searchLower = searchTerm.toLowerCase().trim();
            const matchingRows = initialData.filter((row: any) =>
                String(row[nameField] || '').toLowerCase().includes(searchLower)
            );

            const parentsToExpand = new Set<any>();
            matchingRows.forEach((row: any) => {
                let curr = row;
                while (curr && curr[parentIdField] !== null && curr[parentIdField] !== undefined) {
                    parentsToExpand.add(curr[parentIdField]);
                    curr = initialData.find(p => p[idField] === curr[parentIdField]);
                }
            });

            setExpandedRows(prev => {
                const newExpanded = { ...prev };
                parentsToExpand.forEach(parentId => {
                    newExpanded[parentId] = true;
                });
                return newExpanded;
            });
        } else if (!defaultExpandAll) {
            setExpandedRows({});
        }
    }, [searchTerm, initialData, nameField, parentIdField, idField, defaultExpandAll]);

    // Reset selection on search removed as it causes re-render focus issues

    useEffect(() => {
        if (defaultExpandAll && initialData && initialData.length > 0) {
            const allParents: Record<any, boolean> = {};
            initialData.forEach((row: any) => {
                if (initialData.some((child: any) => child[parentIdField] === row[idField])) {
                    allParents[row[idField]] = true;
                }
            });
            setExpandedRows(prev => ({ ...prev, ...allParents }));
        }
    }, [initialData, defaultExpandAll, idField, parentIdField]);

    const hasChildren = (id: any): boolean => {
        return initialData.some((row: any) => row[parentIdField] === id);
    };

    const toggleRow = (id: any): void => {
        setExpandedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const enhancedColumns: GridColDef[] = useMemo(() => columns.map((col) => {
        const isTreeColumn = col.field === treeColumn;
        const shouldIndent = indentColumns.includes(col.field) || isTreeColumn;

        if (shouldIndent) {
            return {
                ...col,
                renderCell: (params: GridRenderCellParams) => {
                    const isChild = params.row[parentIdField] !== null && params.row[parentIdField] !== undefined;
                    const showExpandIcon = hasChildren(params.row[idField]);
                    const depth = 1; // Basic tree depth for now, can be expanded if needed

                    const isFirstIndented = indentColumns.length > 0 ? col.field === indentColumns[0] : isTreeColumn;

                    const originalRender = col.renderCell ? col.renderCell(params) : params.value;

                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                                position: "relative",
                                paddingLeft: (isChild && shouldIndent) ? 16 : 0,
                            }}
                        >
                            {isFirstIndented && isChild && useLConnector && (
                                <div
                                    style={{
                                        position: "absolute",
                                        left: 4,
                                        top: 0,
                                        width: 10,
                                        height: "50%",
                                        borderLeft: "1.5px solid #C4C4C4",
                                        borderBottom: "1.5px solid #C4C4C4",
                                        borderBottomLeftRadius: "8px",
                                        pointerEvents: "none",
                                    }}
                                />
                            )}

                            {isTreeColumn && (
                                showExpandIcon ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(params.row[idField]);
                                        }}
                                        style={{
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            marginRight: 4,
                                            zIndex: 1,
                                        }}
                                    >
                                        {expandedRows[params.row[idField]] ? (
                                            <KeyboardArrowDown fontSize="small" />
                                        ) : (
                                            <KeyboardArrowRight fontSize="small" />
                                        )}
                                    </div>
                                ) : (
                                    isChild && useSubdirectoryArrow && (
                                        <div style={{ width: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: 4 }}>
                                            <div style={{
                                                width: 0,
                                                height: 0,
                                                borderTop: '4px solid transparent',
                                                borderBottom: '4px solid transparent',
                                                borderLeft: '4px solid #C4C4C4'
                                            }} />
                                        </div>
                                    )
                                )
                            )}

                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                }}
                            >
                                {originalRender}
                            </span>
                        </div>
                    );
                },
            };
        }
        return col;
    }), [columns, treeColumn, indentColumns, expandedRows, initialData, idField, parentIdField, useLConnector, useSubdirectoryArrow]);

    const getVisibleRows = (term?: string, rootsToUse?: any[]): any[] => {
        const sourceData = initialData;
        const roots = rootsToUse || sourceData.filter((row: any) => row[parentIdField] === null || row[parentIdField] === undefined);

        if (!term || term.trim() === '') {
            const result: any[] = [];
            const addVisibleChildren = (parent: any) => {
                result.push(parent);
                if (expandedRows[parent[idField]]) {
                    const children = sourceData.filter((row: any) => row[parentIdField] === parent[idField]);
                    children.forEach((child: any) => addVisibleChildren(child));
                }
            };

            roots.forEach((root: any) => addVisibleChildren(root));
            return result;
        }

        const searchLower = term.toLowerCase().trim();
        const matchingRows = sourceData.filter((row: any) =>
            String(row[nameField] || '').toLowerCase().includes(searchLower)
        );

        const rowsToShow = new Set<any>();
        matchingRows.forEach((row: any) => {
            rowsToShow.add(row[idField]);
            let curr = row;
            while (curr && curr[parentIdField] !== null && curr[parentIdField] !== undefined) {
                const parent = sourceData.find(p => p[idField] === curr[parentIdField]);
                if (parent) {
                    rowsToShow.add(parent[idField]);
                    curr = parent;
                } else break;
            }
            const children = sourceData.filter((child: any) => child[parentIdField] === row[idField]);
            children.forEach((child: any) => rowsToShow.add(child[idField]));
        });

        const result: any[] = [];
        const addParentAndChildren = (parentId: any | null) => {
            const children = sourceData.filter((row: any) =>
                (row[parentIdField] === parentId) && rowsToShow.has(row[idField])
            );

            children.forEach((child: any) => {
                result.push(child);
                addParentAndChildren(child[idField]);
            });
        };

        addParentAndChildren(null);

        // If paginating search results, we might still want to slice? 
        // But let's follow the root logic if possible.
        return result;
    };

    const allRoots = useMemo(() => {
        if (!initialData) return [];
        return initialData.filter((row: any) => row[parentIdField] === null || row[parentIdField] === undefined);
    }, [initialData, parentIdField]);

    const visibleRows = useMemo(() => {
        if (!initialData) return [];
        if (!pagination || (searchTerm && searchTerm.trim() !== '')) {
            return getVisibleRows(searchTerm);
        }

        const start = paginationModel.page * paginationModel.pageSize;
        const rootsForPage = allRoots.slice(start, start + paginationModel.pageSize);
        return getVisibleRows('', rootsForPage);
    }, [searchTerm, initialData, expandedRows, allRoots, pagination, paginationModel]);

    const actionColumn: GridColDef = {
        field: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        renderHeader: () => {
            if (!headerActionMenuItems || headerActionMenuItems.length === 0) return null;
            return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ByzMuiOpenProcessMenu
                        menuItems={headerActionMenuItems}
                        inSearch={true}
                    />
                </div>
            );
        },
        renderCell: (params: GridRenderCellParams) => {
            if (!actionMenuItems) return null;
            const items = actionMenuItems(params.row).map(item => ({
                ...item,
                onClick: () => item.onClick(params.row)
            }));
            if (items.length === 0) return null;

            return (
                <ByzMuiOpenProcessMenu
                    menuItems={items}
                    hideHeaderTitle={true}
                    inSearch={false}
                />
            );
        },
    };

    const finalColumns = useMemo(() => {
        let cols = [...enhancedColumns];
        if (isActionColumnShow) {
            if (actionColumnPosition === "end") {
                cols = [...cols, actionColumn];
            } else {
                cols = [actionColumn, ...cols];
            }
        }
        return cols;
    }, [enhancedColumns, isActionColumnShow, actionColumnPosition, actionColumn]);

    const handleRowSelectionModelChange = (newSelection: any) => {
        const ids = Array.isArray(newSelection) ? newSelection : [newSelection];
        setSelectedIds(ids);
        if (onSelectionChange) onSelectionChange(ids);
    };

    const paginationProps = showFooter && pagination
        ? {
            pagination: true as const,
            paginationModel,
            onPaginationModelChange: setPaginationModel,
            pageSizeOptions,
        }
        : {};

    return (
        <DataGrid
            initialState={initialState}
            rows={visibleRows}
            autoHeight={autoHeight}
            columns={finalColumns}
            checkboxSelection={checkboxSelection}
            disableRowSelectionOnClick={checkboxSelection}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            onRowClick={(params) => onRowClick?.(params.row)}
            isRowSelectable={isRowSelectable}
            getRowClassName={getRowClassName}
            getRowId={(row) => row[idField]}
            disableColumnMenu
            loading={loading}
            paginationMode={pagination ? "server" : "client"}
            sortingMode={sortingMode}
            onSortModelChange={onSortModelChange}
            rowCount={searchTerm && searchTerm.trim() !== '' ? visibleRows.length : allRoots.length}
            localeText={{
                toolbarColumns: t("grid-buttons.columns"),
                toolbarFilters: t("grid-buttons.filter"),
                toolbarDensity: t("grid-buttons.density"),
                toolbarExport: t("grid-buttons.export"),
                noRowsLabel: t("grid-buttons.no-rows-label"),
                MuiTablePagination: {
                    labelRowsPerPage: t("grid-buttons.rows-per-page"),
                },
            }}
            slots={{
                toolbar: InternalToolbar,
            }}
            slotProps={{
                toolbar: {
                    customToolbar,
                    searchTerm,
                    onSearchChange: setSearchTerm,
                    showDefaultToolbarButtons,
                    showQuickFilter: false,
                    ...rest // Pass any other props passed to the grid
                },
            }}
            sx={{
                ...(hideColumns && {
                    "& .MuiDataGrid-columnHeaders": { display: "none" },
                    "& .MuiDataGrid-virtualScroller": { marginTop: "0 !important" },
                }),
                ...(whiteGrid && {
                    border: "none !important",
                    "& .MuiDataGrid-root": { border: "none !important" },
                    "& .MuiDataGrid-main": { border: "none !important" },
                    "& .MuiDataGrid-row": {
                        borderBottom: "1px solid #e5e5e5 !important",
                        "&:last-of-type": {
                            borderBottom: "none !important",
                            "& .MuiDataGrid-cell": { borderBottom: "none !important" },
                        },
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #e5e5e5 !important",
                        display: "flex",
                        alignItems: "center",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid #555 !important",
                        backgroundColor: "white !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                        backgroundColor: "white !important",
                        "&:last-of-type": {
                            borderRight: "none !important",
                        }
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                        color: "#333 !important",
                        fontWeight: "600 !important",
                    },
                    "& .MuiDataGrid-footerContainer": { border: "none !important" },
                    "& .MuiDataGrid-toolbarContainer": {
                        justifyContent: "space-between",
                        alignItems: "center",
                        "& .MuiButton-root": { color: "#309cf3" },
                    },
                }),
                ...sx,
            }}
            {...paginationProps}
            hideFooter={!showFooter}
        />
    );
};

export default ByzDataTreeListGrid;
